import type { IUserMe } from "@api";
import { EnumAppointmentReminderStatus } from "@bbj-types/action/appointmentReminder";
import type { IChallenge } from "@bbj-types/challenge";
import type { ISettingMaster } from "@bbj-types/setting";
import type { ObjectId, Override } from "@bbj-types/utils";
import { tz } from "@date-fns/tz";
import { getActionDate } from "@root/utils/actions";
import { getLocaleTime } from "@root/utils/date/locale";
import { skipWeekend } from "@root/utils/date/skipWeekend";
import { addMinutes, startOfDay } from "date-fns";
import type { DefaultValues } from "react-hook-form";
import { EnumActionDateStatus, computeActionDateStatus } from "../form/AppointmentReminder/logics";
import { formattedNextAction } from "../form/options/nextAction/logics/logics";
import type { IModalActionForm, IModalActionResolver, TModalAction } from "./types";

export const buildApproachesFromChallenges = (challenges: IChallenge[]) => {
  return challenges
    ?.filter((c) => c.approaches)
    .flatMap((c) => c.approaches.map((a) => ({ _id: a._id, label: a.label, challengeName: c.name })));
};

export const formatActionToFormDefaultValues = (
  action: TModalAction | undefined,
  settingActionTypes: ISettingMaster | undefined,
  settingContactChannel: ISettingMaster | undefined,
  challenges: IChallenge[],
  user: IUserMe,
  timeZone: string
): DefaultValues<IModalActionForm> => {
  const currentDate = startOfDay(new Date(), { in: tz(timeZone) });
  const nextDay = new Date(currentDate);
  nextDay.setDate(nextDay.getDate() + 1);
  const nextFollowUpDate = new Date(currentDate);
  nextFollowUpDate.setMonth(nextFollowUpDate.getMonth() + 1);

  const defaultType = action?.type
    ? (settingActionTypes?.values.find((setting) => setting.value === action.type) ?? null)
    : null;

  const defaultChallenges =
    action?.challenges && action.challenges.length > 0
      ? challenges.filter((challenge) => action.challenges?.includes(challenge._id))
      : [];

  const defaultApproach = action?.approach
    ? (buildApproachesFromChallenges(challenges).find((approach) => approach._id === action.approach) ?? null)
    : null;

  const defaultContactChannel = action?.contactChannel
    ? (settingContactChannel?.values.find((setting) => setting.value === action.contactChannel) ?? null)
    : null;

  const defaultHiringFollowUp = action?.apply?.hiringFollowUp?.find((value) => value.action === action._id);

  const actionDate = (action?.status !== undefined && getActionDate({ ...action, status: action.status })) || null;
  const defaultActionStartTime = actionDate ? getLocaleTime(actionDate, timeZone) : null;
  const endDateForTime = action?.approach !== undefined ? action?.executionPeriod?.endDate : action?.endDate;
  const defaultActionEnd = endDateForTime ? getLocaleTime(endDateForTime, timeZone) : null;
  return {
    title: action?.title ?? "",
    type: defaultType,
    challenges: defaultChallenges,
    comments: action?.comments ?? "",
    candidate: action?.candidate ?? null,
    owner: action?.owner ?? user,
    contactChannel: defaultContactChannel,
    date: actionDate ?? currentDate,
    executionPeriod: action?.executionPeriod ?? null,
    startTime: defaultActionStartTime ? defaultActionStartTime.hours * 60 + defaultActionStartTime.minutes || "" : "",
    endTime: defaultActionEnd ? defaultActionEnd.hours * 60 + defaultActionEnd.minutes || "" : "",
    status: action?.status ?? (action?.date && action.date > nextDay ? "todo" : "done"),
    timeSpent: action?.timeSpent ?? "",
    missedReason: action?.missedReason ?? "",
    address: action?.address ?? null,
    jobFollowUp: {
      apply: action?.apply?._id ?? "",
      jobSituation: defaultHiringFollowUp?.situation ?? "",
      nextFollowUpGenerate: true,
      nextFollowUpDate: skipWeekend(nextFollowUpDate),
      endOfContract: defaultHiringFollowUp?.endOfContract ?? null,
    },
    pinned: action?.pinned ?? false,
    sendEventToOwner: Boolean(action?.calendarEvent?.icalEvent?.eventId),
    beneficiaryComment: action?.beneficiaryComment ?? null,
    nextAction: false,
    nextActionDate: null,
    nextActionStartTime: "",
    approach: defaultApproach,
    createdFromCer: action?.createdFromCer ?? false,
    shouldScheduleAppointmentReminder: action?.appointmentReminderStatus === EnumAppointmentReminderStatus.SCHEDULED,
  } satisfies IModalActionForm;
};

const buildDate = (
  data: Override<IModalActionResolver, { candidate?: IModalActionResolver["candidate"] | ObjectId }>,
  timeZone: string
) => {
  const tzCtx = { in: tz(timeZone) };
  const actionDate = addMinutes(startOfDay(data.date ?? new Date(), tzCtx), data.startTime || 0);
  if (data.type.metadata?.approachActionType && data.executionPeriod) {
    return {
      executionPeriod: {
        startDate: addMinutes(startOfDay(data.executionPeriod.startDate as Date, tzCtx), data.startTime || 0),
        endDate: addMinutes(startOfDay(data.executionPeriod.endDate as Date, tzCtx), data.endTime || 0),
      },
    };
  }
  if (data.status === "todo") {
    return { deadlineDate: actionDate };
  }
  return { executionDate: actionDate };
};

export const formatFormDataToAction = (
  data: Override<IModalActionResolver, { candidate?: IModalActionResolver["candidate"] | ObjectId }>,
  timeZone: string
) => {
  const tzCtx = { in: tz(timeZone) };
  const actionDateEnd = addMinutes(startOfDay(data.date ?? new Date(), tzCtx), data.endTime || 0);
  const dates = buildDate(data, timeZone);

  return {
    type: data.type.value,
    candidate: typeof data.candidate === "string" ? data.candidate : data.candidate?._id,
    status: data.status,
    isAutoGenerate: false,
    title: data.title,
    challenges: data.challenges.map((challenge) => challenge._id),
    comments: data.comments,
    owner: data.owner._id,
    ...(data.approach && { approach: data.approach._id }),
    contactChannel: data.contactChannel?.value ?? "",
    ...dates,
    ...(data.timeSpent && { timeSpent: data.timeSpent }),
    ...(data.missedReason && { missedReason: data.missedReason }),
    ...(data.address && { address: data.address }),
    ...(data.jobFollowUp && {
      apply: data.jobFollowUp.apply,
      jobSituation: data.jobFollowUp.jobSituation,
      ...(data.jobFollowUp.endOfContract && { endOfContract: data.jobFollowUp.endOfContract }),
    }),
    ...(!data.type.metadata?.approachActionType && data.endTime && { endDate: actionDateEnd }),
    ...(data.beneficiaryComment && { beneficiaryComment: data.beneficiaryComment }),
    pinned: data.pinned,
    sendEventToOwner: data.sendEventToOwner && data.status === "todo",
    ...formattedNextAction(data),
    createdFromCer: data.createdFromCer ?? false,
    shouldScheduleAppointmentReminder:
      data.status === "todo" &&
      computeActionDateStatus(getActionDate({ status: data.status, ...dates }) ?? null, timeZone) ===
        EnumActionDateStatus.SCHEDULABLE &&
      data.shouldScheduleAppointmentReminder,
  };
};
